/**
 * The loud alert for kitchen and floor staff: a new KOT on the kitchen display,
 * and "food ready" reaching the table's waiter.
 *
 * Browsers only play audio after the person has interacted with the page, so the
 * first tap or key press anywhere unlocks it. If an alert is blocked before that,
 * `subscribeAlertSoundBlocked` tells the app to ask for a tap (SoundUnlockBanner).
 */

import type { WsNotificationEvent } from "@/types/notification";

export const ALERT_SOUND_URL = "/sounds/alert-1.mp3";
/** Two alerts closer than this play once (e.g. a WebSocket event and a poll for the same ticket). */
export const ALERT_DEBOUNCE_MS = 1500;
const KDS_SOUND_KEY = "dhaba.kds.sound";

let audio: HTMLAudioElement | null = null;
let unlocked = false;
let blocked = false;
let lastPlayedAt = 0;
let kitchenBoardOpen = 0;
const blockedListeners = new Set<(blocked: boolean) => void>();

function getAudio(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (!audio) {
    audio = new Audio(ALERT_SOUND_URL);
    audio.preload = "auto";
    audio.volume = 1;
  }
  return audio;
}

function setBlocked(next: boolean) {
  if (blocked === next) return;
  blocked = next;
  blockedListeners.forEach((listener) => listener(next));
}

/** Play the alert at full volume. Safe to call anywhere; never throws. */
export async function playAlertSound(now: number = Date.now()): Promise<boolean> {
  if (now - lastPlayedAt < ALERT_DEBOUNCE_MS) return false;
  const el = getAudio();
  if (!el) return false;
  lastPlayedAt = now;
  try {
    el.muted = false;
    el.volume = 1;
    el.currentTime = 0;
    await el.play();
    setBlocked(false);
    return true;
  } catch {
    // NotAllowedError: no tap on the page yet. Ask for one.
    setBlocked(!unlocked);
    return false;
  }
}

/** Called from the first tap / key press: a muted play unlocks audio for the rest of the session. */
export async function unlockAlertSound(): Promise<void> {
  if (unlocked) return;
  const el = getAudio();
  if (!el) return;
  try {
    el.muted = true;
    await el.play();
    el.pause();
    el.currentTime = 0;
    unlocked = true;
    setBlocked(false);
  } catch {
    // Still blocked; the next gesture tries again.
  } finally {
    el.muted = false;
  }
}

export function subscribeAlertSoundBlocked(listener: (blocked: boolean) => void): () => void {
  blockedListeners.add(listener);
  return () => blockedListeners.delete(listener);
}

export const isAlertSoundBlocked = () => blocked;

/** Kitchen display sound switch, remembered on this device (on by default). */
export function isKitchenSoundEnabled(): boolean {
  try {
    return localStorage.getItem(KDS_SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setKitchenSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(KDS_SOUND_KEY, enabled ? "on" : "off");
  } catch {
    // Private mode: the switch still works for this page.
  }
}

/**
 * While the kitchen display is open it plays its own sound for new tickets, so the
 * `kot_alert` notification must not play a second one.
 */
export function markKitchenBoardOpen(): () => void {
  kitchenBoardOpen += 1;
  return () => {
    kitchenBoardOpen = Math.max(0, kitchenBoardOpen - 1);
  };
}

export const isKitchenBoardOpen = () => kitchenBoardOpen > 0;

/** Which (non-silent, so addressed to this person) notifications sound the alert. */
export function shouldPlayAlert(event: Pick<WsNotificationEvent, "type" | "priority" | "silent">): boolean {
  if (event.silent) return false;
  if (event.type === "kot_ready") return true;
  // A guest at a QR table called a waiter or asked for the bill.
  if (event.type === "table_assist") return true;
  if (event.type === "kot_alert") return !isKitchenBoardOpen();
  return event.priority === "urgent";
}

/** Ids in `next` that were not in `seen` (new tickets). The first load returns none. */
export function newIds(seen: Set<string> | null, next: string[]): string[] {
  if (seen === null) return [];
  return next.filter((id) => !seen.has(id));
}

/** Test hook: forget state between tests. */
export function resetAlertSoundForTests(): void {
  audio = null;
  unlocked = false;
  blocked = false;
  lastPlayedAt = 0;
  kitchenBoardOpen = 0;
  blockedListeners.clear();
}

if (typeof window !== "undefined") {
  const unlock = () => {
    void unlockAlertSound();
  };
  window.addEventListener("pointerdown", unlock, { capture: true });
  window.addEventListener("keydown", unlock, { capture: true });
}
